'use client';

import classNames from 'classnames';
import React, { useEffect, useState } from 'react';

import { StrapiCityDatum, StrapiCityDatumAttributes } from '@/apis/strapi';
import { doTranslation } from '@/app/i18n/client';
import { default as Image } from '@/components/global/images/resized-cdn-image';

import styles from '@/components/schedule-results/city-information-client.module.scss';

type Props = {
    destinationCityData: StrapiCityDatum;
    lng: string;
    originCityData: StrapiCityDatum;
};

const CityInformation = (props: Props): React.JSX.Element => {
    const { t } = doTranslation(props.lng);

    const [isTablet, setIsTablet] = useState(
        () => typeof window !== 'undefined' && window.innerWidth <= 1279 && window.innerWidth > 600
    );
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 600);

    useEffect(() => {
        const handleResize = () => {
            setIsTablet(window.innerWidth <= 1279 && window.innerWidth > 600);
            setIsMobile(window.innerWidth <= 600);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [isTablet, isMobile]);

    const isTabletOrMobile = isTablet || isMobile;

    const getAltText = (cityData: StrapiCityDatum) => {
        const name = getName(cityData);
        return t('Scenic photo of {{ name }}', { name });
    };

    const getName = (cityData: StrapiCityDatum) => {
        return [cityData.attributes.city_name, cityData.attributes?.state?.data.attributes.state_id || ''].join(', ');
    };

    const getDescription = (cityData: StrapiCityDatum) => {
        if (!cityData?.attributes.city_description_short) {
            return [];
        }
        return (
            cityData?.attributes.city_description_short
                .filter((d: any) => {
                    return d?.children[0]?.text.trim() !== '';
                })
                .map((d: any) => {
                    return d?.children[0]?.text;
                }) || []
        );
    };

    // Function to get different photo layouts based on the marketing_images data
    const getImagesAndLayout = (marketingImages: StrapiCityDatumAttributes['marketing_images']) => {
        if (!marketingImages?.data) {
            return { layout: '', data: [] };
        }
        const { data } = marketingImages;

        if (isTabletOrMobile) {
            if (data.length === 1) {
                return { layout: 'tablet', data };
            }
            const squareImages = data.filter((img) => img.attributes.width === img.attributes.height);
            const remainingImages = data.filter((img) => img.attributes.width !== img.attributes.height);
            return { layout: 'tablet', data: [...squareImages, ...remainingImages] };
        }

        if (data.length === 1) {
            return { layout: 'full', data };
        }
        if (data.length === 2) {
            const bothTall = data.every((img) => img.attributes.height > img.attributes.width);
            if (bothTall) {
                return { layout: 'double-height-two', data };
            }
            const bothWide = data.every((img) => img.attributes.height < img.attributes.width);
            if (bothWide) {
                return { layout: 'double-width-two', data };
            }
            return { layout: 'square-or-mixed-two', data };
        }

        let tallOrWideImage = data.find((img) => img.attributes.height > img.attributes.width);
        if (!tallOrWideImage) {
            tallOrWideImage = data.find((img) => img.attributes.width > img.attributes.height);
        }
        const remainingImages = data.filter((img) => img !== tallOrWideImage);
        if (remainingImages.length >= 2 && tallOrWideImage) {
            return {
                layout:
                    tallOrWideImage.attributes.height > tallOrWideImage.attributes.width
                        ? 'double-height-three'
                        : 'double-width-three',
                data: [remainingImages[0], remainingImages[1], tallOrWideImage]
            };
        }

        const allSquare = data.every((img) => img.attributes.height === img.attributes.width);
        if (allSquare && data.length >= 4) {
            return { layout: 'all-square', data };
        }

        return { layout: 'double-height-three', data };
    };

    const originAndDestinationSame =
        props.originCityData.attributes.city_slug === props.destinationCityData.attributes.city_slug;

    const originDescription = getDescription(props.originCityData);
    const destinationDescription = getDescription(props.destinationCityData);

    const originImages = getImagesAndLayout(props.originCityData.attributes?.marketing_images);
    const destinationImages = getImagesAndLayout(props.destinationCityData.attributes?.marketing_images);

    if (!originDescription.length && !destinationDescription.length) {
        return <></>;
    }

    if (!originImages.data.length && !destinationImages.data.length) {
        return <></>;
    }

    const renderImages = (type: string) => {
        const images = type === 'origin' ? originImages : destinationImages;
        const cityData = type === 'origin' ? props.originCityData : props.destinationCityData;
        const altText = getAltText(cityData);
        const { data } = images;


        if (isTabletOrMobile) {
            return (
                <div className={styles.images}>
                    {data.slice(0, isTablet ? 3 : 1).map((image) => (
                        <Image
                            alt={image.attributes.alternativeText || altText}
                            height={image.attributes.formats.small.height}
                            key={image.attributes.hash}
                            loading="lazy"
                            src={image.attributes.formats.small.url}
                            width={image.attributes.formats.small.width}
                        />
                    ))}
                </div>
            );
        }

        if (images.layout === 'full') {
            return (
                <div className={styles.full}>
                    <Image
                        alt={data[0].attributes.alternativeText || altText}
                        height={data[0].attributes.formats.medium.height}
                        loading="lazy"
                        src={data[0].attributes.formats.medium.url}
                        width={data[0].attributes.formats.medium.width}
                    />
                </div>
            );
        }
        if (images.layout === 'square-or-mixed-two') {
            return (
                <div className={styles['double-height']}>
                    <div className={styles.grid}>
                        <div className={styles.multiple}>
                            <div className={styles.top}>
                                <Image
                                    alt={data[0].attributes.alternativeText || altText}
                                    height={data[0].attributes.formats.small.height}
                                    loading="lazy"
                                    src={data[0].attributes.formats.small.url}
                                    width={data[0].attributes.formats.small.width}
                                />
                            </div>
                            <div className={styles.bottom}>
                                <Image
                                    alt={data[1].attributes.alternativeText || altText}
                                    height={data[1].attributes.formats.small.height}
                                    loading="lazy"
                                    src={data[1].attributes.formats.small.url}
                                    width={data[1].attributes.formats.small.width}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
        if (images.layout === 'double-height-two') {
            return (
                <div className={styles['double-height']}>
                    <div className={styles.grid}>
                        <div className={styles.single}>
                            <Image
                                alt={data[0].attributes.alternativeText || altText}
                                height={data[0].attributes.formats.medium.height}
                                loading="lazy"
                                src={data[0].attributes.formats.medium.url}
                                width={data[0].attributes.formats.medium.width}
                            />
                        </div>
                        <div className={styles.single}>
                            <Image
                                alt={data[1].attributes.alternativeText || altText}
                                height={data[1].attributes.formats.medium.height}
                                loading="lazy"
                                src={data[1].attributes.formats.medium.url}
                                width={data[1].attributes.formats.medium.width}
                            />
                        </div>
                    </div>
                </div>
            );
        }
        if (images.layout === 'double-height-three') {
            return (
                <div className={styles['double-height']}>
                    <div
                        className={classNames({
                            [styles.grid]: true,
                            [styles.reverse]: type === 'destination'
                        })}
                    >
                        <div className={styles.multiple}>
                            <div className={styles.top}>
                                <Image
                                    alt={data[0].attributes.alternativeText || altText}
                                    height={data[0].attributes.formats.small.height}
                                    loading="lazy"
                                    src={data[0].attributes.formats.small.url}
                                    width={data[0].attributes.formats.small.width}
                                />
                            </div>
                            <div className={styles.bottom}>
                                <Image
                                    alt={data[1].attributes.alternativeText || altText}
                                    height={data[1].attributes.formats.small.height}
                                    loading="lazy"
                                    src={data[1].attributes.formats.small.url}
                                    width={data[1].attributes.formats.small.width}
                                />
                            </div>
                        </div>
                        <div className={styles.single}>
                            <Image
                                alt={data[2].attributes.alternativeText || altText}
                                height={data[2].attributes.formats.medium.height}
                                loading="lazy"
                                src={data[2].attributes.formats.medium.url}
                                width={data[2].attributes.formats.medium.width}
                            />
                        </div>
                    </div>
                </div>
            );
        }
        if (images.layout === 'double-width-two') {
            return (
                <div className={styles['double-width']}>
                    <div className={styles.grid}>
                        <div className={styles.single}>
                            <Image
                                alt={data[0].attributes.alternativeText || altText}
                                height={data[0].attributes.formats.medium.height}
                                loading="lazy"
                                src={data[0].attributes.formats.medium.url}
                                width={data[0].attributes.formats.medium.width}
                            />
                        </div>
                        <div className={styles.single}>
                            <Image
                                alt={data[1].attributes.alternativeText || altText}
                                height={data[1].attributes.formats.medium.height}
                                loading="lazy"
                                src={data[1].attributes.formats.medium.url}
                                width={data[1].attributes.formats.medium.width}
                            />
                        </div>
                    </div>
                </div>
            );
        }
        if (images.layout === 'double-width-three') {
            return (
                <div className={styles['double-width']}>
                    <div className={styles.grid}>
                        <div className={styles.single}>
                            <Image
                                alt={data[2].attributes.alternativeText || altText}
                                height={data[2].attributes.formats.medium.height}
                                loading="lazy"
                                src={data[2].attributes.formats.medium.url}
                                width={data[2].attributes.formats.medium.width}
                            />
                        </div>
                        <div className={styles.multiple}>
                            <div className={styles.left}>
                                <Image
                                    alt={data[0].attributes.alternativeText || altText}
                                    height={data[0].attributes.formats.small.height}
                                    loading="lazy"
                                    src={data[0].attributes.formats.small.url}
                                    width={data[0].attributes.formats.small.width}
                                />
                            </div>
                            <div className={styles.right}>
                                <Image
                                    alt={data[1].attributes.alternativeText || altText}
                                    height={data[1].attributes.formats.small.height}
                                    loading="lazy"
                                    src={data[1].attributes.formats.small.url}
                                    width={data[1].attributes.formats.small.width}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
        if (images.layout === 'all-square') {
            return (
                <div className={styles['double-height']}>
                    <div className={styles.grid}>
                        <div className={styles.multiple}>
                            <div className={styles.top}>
                                <Image
                                    alt={data[0].attributes.alternativeText || altText}
                                    height={data[0].attributes.formats.small.height}
                                    loading="lazy"
                                    src={data[0].attributes.formats.small.url}
                                    width={data[0].attributes.formats.small.width}
                                />
                            </div>
                            <div className={styles.bottom}>
                                <Image
                                    alt={data[1].attributes.alternativeText || altText}
                                    height={data[1].attributes.formats.small.height}
                                    loading="lazy"
                                    src={data[1].attributes.formats.small.url}
                                    width={data[1].attributes.formats.small.width}
                                />
                            </div>
                        </div>
                        <div className={styles.multiple}>
                            <div className={styles.top}>
                                <Image
                                    alt={data[2].attributes.alternativeText || altText}
                                    height={data[2].attributes.formats.small.height}
                                    loading="lazy"
                                    src={data[2].attributes.formats.small.url}
                                    width={data[2].attributes.formats.small.width}
                                />
                            </div>
                            <div className={styles.bottom}>
                                <Image
                                    alt={data[3].attributes.alternativeText || altText}
                                    height={data[3].attributes.formats.small.height}
                                    loading="lazy"
                                    src={data[3].attributes.formats.small.url}
                                    width={data[3].attributes.formats.small.width}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
    };

    return (
        <div
            aria-label={t('Learn more about your origin and destination cities')}
            className={styles.container}
            role="region"
        >
            {originDescription.length && originImages.data.length ? (
                <div className={styles.info}>
                    <div className={styles.origin}>
                        <div className={styles.block}>
                            <h2 className={styles.title}>
                                <span className={styles.text}>{t('City Information')}</span>
                                <span className="sr-only">{t('for')}</span>
                                <span className={styles.heading}>{getName(props.originCityData)}</span>
                            </h2>
                            <div className={styles.overview}>
                                <div className={styles.description}>
                                    <p>{originDescription[0] || ''}</p>
                                    {!isTabletOrMobile && <p>{originDescription[1] || ''}</p>}
                                </div>
                            </div>
                        </div>
                        {renderImages('origin')}
                    </div>
                </div>
            ) : null}

            {destinationDescription.length && destinationImages.data.length && !originAndDestinationSame ? (
                <div className={styles.info}>
                    <div className={styles.origin}>
                        {!isTabletOrMobile && renderImages('destination')}
                        <div className={styles.block}>
                            <h2 className={styles.title}>
                                <span className={styles.text}>{t('City Information')}</span>
                                <span className="sr-only">{t('for')}</span>
                                <span className={styles.heading}>{getName(props.destinationCityData)}</span>
                            </h2>
                            <div className={styles.overview}>
                                <div className={styles.description}>
                                    <p>{destinationDescription[0] || ''}</p>
                                    {!isTabletOrMobile && <p>{destinationDescription[1] || ''}</p>}
                                </div>
                            </div>
                        </div>
                        {isTabletOrMobile && renderImages('destination')}
                    </div>
                </div>
            ) : null}
        </div>
    );
};
export default CityInformation;
